import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ReactComponent as ArrowLeftIcon } from '../icons/arrow.left.svg';
import { ReactComponent as XmarkCircleIcon } from '../icons/xmark.circle.svg';
import { loginWithCode } from '../data/repositories/LoginWithCode';
import {
  getSocialLoginErrorMessage,
  isSocialLoginSuccess,
  socialLoginRequestVerificationCode,
  socialLoginVerify
} from '../data/repositories/SocialLogin';
import { requestVerificationCode } from '../data/repositories/RequestVerificationCode';
import { completeAuthLogin } from '../utils/completeAuthLogin';

const verificationCodePlaceholder = t('loginWithCodePage.verificationCodePlaceholder');

interface LoginWithCodePageState {
  email?: string;
  maskedEmail?: string;
  isPromotionAgreed?: boolean;
  socialSessionToken?: string;
  socialSelectedEmail?: string;
}

export default function LoginWithCodePage() {
  const location = useLocation();
  const state = (location.state as LoginWithCodePageState | null) ?? null;
  const email = state?.email ?? '';
  const displayEmail = state?.maskedEmail || email;
  const socialSessionToken = state?.socialSessionToken;
  const socialSelectedEmail = state?.socialSelectedEmail || email;
  const isSocialVerificationFlow = Boolean(socialSessionToken);
  const [input, setInput] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) {
      navigate('/request-verification-code?init=true', { replace: true });
    }
  }, [email, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value);

  const onBackClick = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      return;
    }
  };

  const clearInput = () => {
    setInput('');
  };

  const postVerificationCode = async () => {
    if (!email) {
      alert(t('requestFailed'));
      return;
    }

    if (isSocialVerificationFlow && socialSessionToken) {
      const socialResponse = await socialLoginVerify({
        socialSessionToken,
        verificationCode: input
      });

      if (isSocialLoginSuccess(socialResponse)) {
        const completed = completeAuthLogin({
          response: socialResponse,
          navigate,
          fallbackEmail: email
        });

        if (!completed) {
          alert(t('loginWithCodePage.verificationFailedAlert'));
        }
        return;
      }

      alert(
        getSocialLoginErrorMessage(socialResponse) || t('loginWithCodePage.verificationFailedAlert')
      );
      return;
    }

    const response = await loginWithCode(email, input);

    if (response?.success && 'jwt' in response && response.jwt) {
      const completed = completeAuthLogin({
        response,
        navigate,
        fallbackEmail: email
      });

      if (!completed) {
        alert(t('loginWithCodePage.verificationFailedAlert'));
      }
    } else {
      alert(t('loginWithCodePage.verificationFailedAlert'));
    }
  };

  const reRequestVerificationCode = () => {
    if (!email) {
      alert(t('requestFailed'));
      return;
    }

    if (isSocialVerificationFlow && socialSessionToken) {
      socialLoginRequestVerificationCode({
        socialSessionToken,
        selectedEmail: socialSelectedEmail
      }).then((r) => {
        if (r.success) {
          alert(t('loginWithCodePage.successSendAlert'));
        } else {
          alert(getSocialLoginErrorMessage(r) || t('loginWithCodePage.failedSendAlert'));
        }
      });
      return;
    }

    requestVerificationCode(email, Boolean(state?.isPromotionAgreed)).then((r) => {
      if (r.success) {
        alert(t('loginWithCodePage.successSendAlert'));
        return;
      }

      alert(t('loginWithCodePage.failedSendAlert'));
    });
  };

  if (!email) {
    return <div />;
  }

  return (
    <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark items-center w-[100vw] max-w-[570px] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <button
        className="mt-[14px] px-[16px] mb-[15px] w-full z-50 justify-start"
        onClick={onBackClick}
        type="button"
      >
        <ArrowLeftIcon className="fill-black dark:fill-white" />
      </button>
      <div className="flex flex-col items-center w-[100vw] max-w-[570px] px-[24px]">
        <div className="flex flex-col mt-[12px] w-full">
          <span className="text-left text-[24px] font-bold line-height-[120%]">
            {t('loginWithCodePage.title')}
          </span>
          <span className="text-main-light-4 dark:text-main-dark-4 mt-2 text-[15px]">
            {t('loginWithCodePage.description', { email: displayEmail })}
          </span>
        </div>
        <div className="relative mt-[52px] w-full">
          <input
            type="number"
            value={input}
            onChange={handleInputChange}
            placeholder={verificationCodePlaceholder}
            className="
              w-full
              bg-secondary-light dark:bg-secondary-dark
              hover:bg-secondary-light dark:hover:bg-secondary-dark
              rounded-[12px]
              py-[14px] px-[16px]
              text-gray-900 dark:text-gray-100
              placeholder-gray-400 dark:placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark
              border
              border-transparent
            "
          />
          {input && (
            <button
              type="button"
              onClick={clearInput}
              className="
                  absolute right-[16px] top-1/2
                  -translate-y-1/2
                  w-[24px] h-[24px]
                  flex items-center justify-center
                "
            >
              <XmarkCircleIcon fill="gray" width={24} height={24} />
            </button>
          )}
        </div>
        <div className="mt-[20px]">
          <span className="text-onSecondary-light font-[13px] dark:text-onSecondary-dark">
            {t('loginWithCodePage.notReceivedMessage')}
            <button
              type="button"
              style={{ textDecoration: 'underline', paddingLeft: '3px' }}
              onClick={() => reRequestVerificationCode()}
            >
              {t('loginWithCodePage.sendAgainMessage')}
            </button>
          </span>
        </div>
        <div className="mt-auto w-full max-w-[570px] absolute bottom-[5%] px-[24px]">
          <button
            className={`
            text-white ${
              input !== ''
                ? 'bg-primary-light dark:bg-primary-dark text-white'
                : 'bg-disabled-light dark:bg-disabled-dark text-onDisabled-light dark:text-onDisabled-dark'
            }
            flex items-center justify-center
            w-full
            font-bold
            text-[16px]
            text-center
            rounded-[56px]
            disabled:opacity-50
            min-h-[52px]
          `}
            onClick={postVerificationCode}
            type="button"
          >
            {t('next')}
          </button>
        </div>
      </div>
    </div>
  );
}
